'use client';

import { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import { Text, useTranslation } from '@weareconceptstudio/core';
import Link from 'next/link';
import gsap from 'gsap/all';

import { withUIContext } from '@/context';
import { LOCALES } from '@/constants';

import LanguageSwitcherStyle from './style';

const LanguageSwitcher = ({ isTouchScreen }) => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { selectedLang } = useTranslation();

	const [isDropDownActive, setIsDropdownActive] = useState(false);
	const dropdownRef = useRef(null);
	const langTextRef = useRef(null);

	const storeLanguages = useMemo(() => {
		const supportedLangs = LOCALES;

		return supportedLangs.map((lang) => {
			const route = pathname.replace(`/${selectedLang}`, '');
			const updatedSearchParams = new URLSearchParams(searchParams.toString());

			const rParam = updatedSearchParams.get('r');
			if (rParam && supportedLangs.some((l) => rParam.startsWith(`/${l}/`))) {
				const newR = rParam.replace(`/${selectedLang}/`, `/${lang}/`);
				updatedSearchParams.set('r', newR);
			}

			let newUrl = `/${lang}${route}`;
			const queryString = updatedSearchParams.toString();

			if (queryString.length) {
				newUrl = `${newUrl}?${queryString}`;
			}

			return selectedLang !== lang ? (
				<div
					key={lang}
					className='lang-item item-language'>
					<Link
						locale={lang}
						as={newUrl}
						href={`/[lang]${route}`}>
						<Text
							className='p p5 capitalize'
							text={lang}
						/>
					</Link>
				</div>
			) : null;
		});
	}, [selectedLang, pathname, searchParams]);

	function clickMouse() {
		if (isTouchScreen) setIsDropdownActive(!isDropDownActive);
	}

	function moveMouse() {
		if (!isDropDownActive) setIsDropdownActive(true);
	}

	function leaveMouse() {
		if (isDropDownActive) setIsDropdownActive(false);
	}

	useEffect(() => {
		if (!dropdownRef.current || !langTextRef.current) return;

		if (isDropDownActive) {
			gsap.to(dropdownRef.current, { height: 'auto', duration: 0.3, ease: 'power2.out' });
			gsap.to(langTextRef.current, { scale: 0.9, duration: 0.3, ease: 'power2.out' });
		} else {
			gsap.to(dropdownRef.current, { height: 0, duration: 0.3, ease: 'power2.out' });
			gsap.to(langTextRef.current, { scale: 1, duration: 0.3, ease: 'power2.out' });
		}
	}, [isDropDownActive]);

	useEffect(() => {
		if (!isDropDownActive) return undefined;

		const onScroll = () => setIsDropdownActive(false);

		window.addEventListener('scroll', onScroll, { passive: true, capture: true });

		return () => {
			window.removeEventListener('scroll', onScroll, { capture: true });
		};
	}, [isDropDownActive]);

	useEffect(() => {
		document.body.classList.toggle('is-lang-dropdown-open', isDropDownActive);
		return () => document.body.classList.remove('is-lang-dropdown-open');
	}, [isDropDownActive]);

	return (
		<LanguageSwitcherStyle className={isDropDownActive ? 'is-dropdown-open' : ''}>
			<div className='inner-wrapper-language'>
				<div className='selected-lang-wrap'>
					<div
						onClick={clickMouse}
						onMouseMove={moveMouse}
						onMouseLeave={leaveMouse}
						className='inner-wrapper-language'>
						<Text
							ref={langTextRef}
							className='capitalize p p5 lang-text'
							text={selectedLang}
						/>

						<div
							ref={dropdownRef}
							className='dropdown'>
							<div className='inner-wrapper-dropdown'>{storeLanguages}</div>
						</div>
					</div>
				</div>
			</div>
		</LanguageSwitcherStyle>
	);
};

export default withUIContext(LanguageSwitcher, ['isTouchScreen']);
